import { supabase } from '../lib/supabase/client';
import { Database } from '../types/database.types';
import { NewsletterSubscription, NewsletterSubscriptionInput } from '../types/models';
import { emailService } from './emailService';

type NewsletterRow = Database['public']['Tables']['newsletter_subscriptions']['Row'];
type NewsletterInsert = Database['public']['Tables']['newsletter_subscriptions']['Insert'];

interface NewsletterResponse {
  data: NewsletterSubscription | null;
  error: Error | null;
}

export const newsletterService = {
  async subscribe(input: NewsletterSubscriptionInput): Promise<NewsletterResponse> {
    try {
      // Convert camelCase to snake_case for DB
      const insertData: NewsletterInsert = {
        email: input.email.toLowerCase().trim(),
        is_active: true,
      };

      const { data, error } = await supabase
        .from('newsletter_subscriptions')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        // Handle duplicate email error gracefully
        if (error.code === '23505') {
          return {
            data: null,
            error: new Error('This email is already subscribed to our newsletter'),
          };
        }
        throw error;
      }

      // Convert snake_case to camelCase for app
      const subscription: NewsletterSubscription = {
        id: data.id,
        email: data.email,
        subscribedAt: data.subscribed_at,
        isActive: data.is_active,
      };

      // Send confirmation email (don't block on email sending)
      emailService.sendNewsletterConfirmation(data.email).catch((err) => {
        // Log email error but don't fail the subscription
        console.error('Email send error:', err);
      });

      // Send notification email to team (don't block on email sending)
      emailService.sendNewsletterSubscriptionNotification(data.email).catch((err) => {
        // Log email error but don't fail the subscription
        console.error('Team notification email error:', err);
      });

      return { data: subscription, error: null };
    } catch (err) {
      return {
        data: null,
        error: err instanceof Error ? err : new Error('Failed to subscribe to newsletter'),
      };
    }
  },

  async unsubscribe(email: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from('newsletter_subscriptions')
        .update({ is_active: false })
        .eq('email', email.toLowerCase().trim());

      if (error) throw error;

      return { error: null };
    } catch (err) {
      return {
        error: err instanceof Error ? err : new Error('Failed to unsubscribe from newsletter'),
      };
    }
  },

  async checkSubscription(email: string): Promise<{ subscribed: boolean; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('newsletter_subscriptions')
        .select('is_active')
        .eq('email', email.toLowerCase().trim())
        .maybeSingle();

      if (error) throw error;

      return { subscribed: data?.is_active ?? false, error: null };
    } catch (err) {
      return {
        subscribed: false,
        error: err instanceof Error ? err : new Error('Failed to check subscription status'),
      };
    }
  },
};